/**
 * Non-prod PKCE / refresh_token diagnostics.
 *
 * Enabled when `window.config.env !== 'prod'`, or force on via:
 *   localStorage.setItem('tc_pkce_refresh_debug', '1')
 *
 * Force off in non-prod:
 *   localStorage.setItem('tc_pkce_refresh_debug', '0')
 */
export const PKCE_REFRESH_DEBUG_KEY = 'tc_pkce_refresh_debug';

type WindowWithConfig = Window & { config?: { env?: string } };

function readDebugOverride(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return localStorage.getItem(PKCE_REFRESH_DEBUG_KEY);
  } catch {
    return null;
  }
}

export function isPkceRefreshDebugEnabled(): boolean {
  const override = readDebugOverride();
  if (override === '0') {
    return false;
  }
  if (override === '1') {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const env = (window as WindowWithConfig).config?.env;
  return env !== undefined && env !== 'prod';
}

export function maskSecret(value: string | undefined, visible = 6): string {
  if (!value) {
    return '(empty)';
  }
  if (value.length <= visible * 2) {
    return value;
  }
  return `${value.slice(0, visible)}…${value.slice(-visible)}`;
}

export function logPkceRefresh(
  message: string,
  details?: Record<string, unknown>
): void {
  if (!isPkceRefreshDebugEnabled()) {
    return;
  }

  if (details === undefined) {
    // eslint-disable-next-line no-console
    console.info('%c[pkce-refresh]', 'color:#862e9c;font-weight:bold', message);
    return;
  }

  // eslint-disable-next-line no-console
  console.info(
    '%c[pkce-refresh]',
    'color:#862e9c;font-weight:bold',
    message,
    details
  );
}

/** Always logs refresh failures in non-prod (even when verbose PKCE debug is off). */
export function logPkceRefreshFailure(
  message: string,
  details?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const env = (window as WindowWithConfig).config?.env;
  const override = readDebugOverride();
  if (env === 'prod' && override !== '1') {
    return;
  }

  if (details === undefined) {
    // eslint-disable-next-line no-console
    console.error(
      '%c[pkce-refresh]',
      'color:#c92a2a;font-weight:bold',
      message
    );
    return;
  }

  // eslint-disable-next-line no-console
  console.error(
    '%c[pkce-refresh]',
    'color:#c92a2a;font-weight:bold',
    message,
    details
  );
}

export function parseOAuthErrorBody(responseBody: string): unknown {
  if (!responseBody) {
    return undefined;
  }

  try {
    return JSON.parse(responseBody) as unknown;
  } catch {
    return responseBody;
  }
}
